// src/components/dashboard/MorningBrief.jsx
// Slide-up Morning Brief — personalized to the user's actual holdings.
// Computed client-side from live data. No scheduled edge function needed.
// Plain English throughout — no unexplained finance jargon.

import { useState, useEffect } from 'react'
import { supabase, hasSupabase } from '../../lib/supabaseClient'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDollar(val) {
  if (val == null) return null
  const abs = Math.abs(val)
  const sign = val >= 0 ? '+' : '-'
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

function holdingName(h) {
  return h.company || h.name || h.ticker
}

// Plain-English action prompts — each references the specific risk driver
// and avoids jargon. Mapping keyed to risk_scores primary_driver values.
const ACTION_PROMPTS = {
  'Geo-political exposure': {
    text: (topHolding) =>
      `Some of your holdings — like ${topHolding} — are in regions with rising political tensions. ` +
      `It might be worth checking whether you're comfortable with that level of exposure.`,
    module: 'Geo-Risk Map',
    tab:    'world',
  },
  'Sector concentration': {
    text: (topHolding) =>
      `A large part of your portfolio is in the same industry (${topHolding} and others). ` +
      `When one sector falls, all your holdings can fall together. ` +
      `Adding stocks from different industries can help reduce that risk.`,
    module: 'Screener',
    tab:    'screener',
  },
  'Asset volatility': {
    text: (topHolding) =>
      `Some of your holdings, including ${topHolding}, tend to swing up and down a lot in price. ` +
      `This isn't necessarily bad, but it means your portfolio value can change quickly. ` +
      `Check your position sizes to make sure you're comfortable with the swings.`,
    module: 'Portfolio Builder',
    tab:    'builder',
  },
  'Currency exposure': {
    text: () =>
      `Most of your holdings are priced in a single currency. ` +
      `If that currency weakens, your overall returns could take a hit even if your stocks perform well. ` +
      `FX Markets shows the latest currency moves.`,
    module: 'FX & Markets',
    tab:    'fxmarkets',
  },
  'Portfolio balance': {
    text: (topHolding) =>
      `Your portfolio is out of balance — some holdings like ${topHolding} take up much more space than others. ` +
      `Rebalancing means adjusting weights so no single stock has an outsized effect on your returns.`,
    module: 'Optimizer',
    tab:    'optimizer',
  },
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Section({ title, children, accent = false }) {
  return (
    <div style={{
      padding: '18px 24px',
      borderBottom: '1px solid #1a2332',
      borderLeft: accent ? '3px solid var(--accent)' : undefined,
      paddingLeft: accent ? 21 : 24,
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 9,
        color: 'var(--text-tertiary)',
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        marginBottom: 10,
      }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function PlainText({ children, size = 13, color }) {
  return (
    <div style={{
      fontFamily: 'var(--font-body)',
      fontSize: size,
      color: color ?? 'var(--text-primary)',
      lineHeight: 1.65,
    }}>
      {children}
    </div>
  )
}

function Mono({ children, size = 13, color }) {
  return (
    <span style={{
      fontFamily: 'var(--font-mono)',
      fontSize: size,
      color: color ?? 'var(--text-primary)',
    }}>
      {children}
    </span>
  )
}

function HoldingRow({ ticker, name, change, isPositive }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6px 0',
    }}>
      <div>
        <Mono size={12} color="var(--text-primary)">{ticker}</Mono>
        {name && (
          <span style={{
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'var(--text-tertiary)',
            marginLeft: 8,
          }}>
            {name}
          </span>
        )}
      </div>
      {change != null && (
        <Mono size={12} color={isPositive ? 'var(--positive)' : 'var(--negative)'}>
          {isPositive ? '+' : ''}{change.toFixed(2)}%
        </Mono>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MorningBrief({
  userId,
  holdings = [],
  dayChange,
  dayChangePct,
  riskData,      // from getOrCalculateRiskScore — may be null
  onClose,
  onNavigate,
}) {
  const [events, setEvents] = useState([])

  // Fetch any upcoming market events relevant to the user's tickers
  useEffect(() => {
    if (!hasSupabase || !userId || !holdings.length) return
    const tickers = holdings.map(h => h.ticker?.toUpperCase()).filter(Boolean)
    const sectors = [...new Set(holdings.map(h => h.sector?.toLowerCase()).filter(Boolean))]

    supabase
      .from('market_events')
      .select('title, event_at, relevant_to')
      .gte('event_at', new Date().toISOString())
      .order('event_at', { ascending: true })
      .limit(20)
      .then(({ data }) => {
        const relevant = (data ?? []).filter(e => {
          const tags = e.relevant_to ?? []
          return tags.some(tag =>
            tickers.includes(tag.toUpperCase()) ||
            sectors.includes(tag.toLowerCase())
          )
        }).slice(0, 4)
        setEvents(relevant)
      })
  }, [userId, holdings.length]) // eslint-disable-line

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', month: 'long', day: 'numeric',
  })

  const hasHoldings = holdings.length > 0

  // Sort holdings by today's % change (need liveChangePct)
  const withChange = holdings
    .filter(h => h.liveChangePct != null)
    .sort((a, b) => b.liveChangePct - a.liveChangePct)

  const best  = withChange[0]  ?? null
  const worst = withChange[withChange.length - 1] ?? null

  // Biggest holding by weight
  const biggestByWeight = [...holdings].sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0))[0] ?? null
  const biggestName = biggestByWeight ? holdingName(biggestByWeight) : 'your largest holding'

  // Action from riskData
  const actionDef = riskData?.primary_driver
    ? ACTION_PROMPTS[riskData.primary_driver] ?? null
    : null
  const actionText = actionDef?.text ? actionDef.text(biggestName) : null

  const portfolioUp   = dayChangePct != null ? dayChangePct >= 0 : null
  const changePctAbs  = dayChangePct != null ? Math.abs(dayChangePct).toFixed(2) : null
  const changeDollar  = dayChange    != null ? fmtDollar(dayChange) : null

  return (
    <>
      <style>{`
        @keyframes mb-slidein {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,.6)',
          zIndex: 900,
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        maxWidth: 640,
        margin: '0 auto',
        background: '#0d1117',
        border: '1px solid #1c2333',
        borderBottom: 'none',
        borderRadius: '16px 16px 0 0',
        zIndex: 901,
        animation: 'mb-slidein .3s cubic-bezier(.32,.72,0,1)',
        maxHeight: '88vh',
        overflowY: 'auto',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 24px',
          borderBottom: '1px solid #1a2332',
          position: 'sticky', top: 0,
          background: '#0d1117',
          zIndex: 1,
        }}>
          <div>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--accent)',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              fontWeight: 700,
            }}>
              Morning Brief
            </span>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--text-tertiary)',
              marginLeft: 12,
              letterSpacing: '.04em',
            }}>
              {today}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none',
              color: 'var(--text-tertiary)',
              fontSize: 18, cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            ✕
          </button>
        </div>

        {!hasHoldings ? (
          <div style={{
            padding: '40px 24px',
            fontFamily: 'var(--font-body)',
            fontSize: 13,
            color: 'var(--text-tertiary)',
            textAlign: 'center',
            lineHeight: 1.6,
          }}>
            Add some holdings to your portfolio to see your personalised brief.
          </div>
        ) : (
          <>
            {/* ── HOW YOUR PORTFOLIO IS DOING ─────────────────────── */}
            <Section title="How Your Portfolio Is Doing">
              {dayChangePct != null ? (
                <PlainText>
                  Your portfolio is{' '}
                  <span style={{ color: portfolioUp ? 'var(--positive)' : 'var(--negative)', fontWeight: 600 }}>
                    {portfolioUp ? 'up' : 'down'} {changePctAbs}%
                  </span>
                  {changeDollar && (
                    <span> ({changeDollar})</span>
                  )}
                  {' '}since your last update. That means your holdings are worth{' '}
                  {portfolioUp ? 'more' : 'less'} than yesterday.
                </PlainText>
              ) : (
                <PlainText color="var(--text-tertiary)">
                  Set your portfolio value on the Dashboard to track day-to-day changes here.
                </PlainText>
              )}
            </Section>

            {/* ── YOUR HOLDINGS TODAY ──────────────────────────────── */}
            {(best || worst) && (
              <Section title="Your Holdings Today">
                {best && best !== worst && (
                  <div style={{ marginBottom: 12 }}>
                    <PlainText size={12} color="var(--text-secondary)">
                      Best performer
                    </PlainText>
                    <HoldingRow
                      ticker={best.ticker}
                      name={holdingName(best)}
                      change={best.liveChangePct}
                      isPositive={true}
                    />
                    <PlainText size={12} color="var(--text-tertiary)">
                      {holdingName(best)} gained the most in your portfolio today.
                      {best.weight != null && ` It makes up ${best.weight.toFixed(1)}% of your portfolio.`}
                    </PlainText>
                  </div>
                )}

                {worst && (
                  <div style={{ marginTop: best && best !== worst ? 12 : 0 }}>
                    <PlainText size={12} color="var(--text-secondary)">
                      Needs attention
                    </PlainText>
                    <HoldingRow
                      ticker={worst.ticker}
                      name={holdingName(worst)}
                      change={worst.liveChangePct}
                      isPositive={worst.liveChangePct >= 0}
                    />
                    {worst.liveChangePct < 0 && (
                      <PlainText size={12} color="var(--text-tertiary)">
                        {holdingName(worst)} dropped the most today. This doesn't mean you should sell — short-term dips are normal. Worth keeping an eye on.
                      </PlainText>
                    )}
                  </div>
                )}
              </Section>
            )}

            {/* ── ALL YOUR HOLDINGS ────────────────────────────────── */}
            {holdings.length > 0 && (
              <Section title={`All Your Holdings (${holdings.length})`}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {holdings.map(h => (
                    <HoldingRow
                      key={h.ticker}
                      ticker={h.ticker}
                      name={holdingName(h)}
                      change={h.liveChangePct}
                      isPositive={(h.liveChangePct ?? 0) >= 0}
                    />
                  ))}
                </div>
              </Section>
            )}

            {/* ── ONE THING TO DO TODAY ────────────────────────────── */}
            {actionDef && actionText && (
              <Section title="One Thing to Do Today" accent>
                <PlainText style={{ marginBottom: 14 }}>
                  {actionText}
                </PlainText>
                <button
                  onClick={() => { onClose(); onNavigate?.(actionDef.tab) }}
                  style={{
                    marginTop: 14,
                    width: '100%',
                    background: 'var(--accent)',
                    border: 'none',
                    color: '#060a0f',
                    padding: '10px 0',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    letterSpacing: '.06em',
                    transition: 'opacity .15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  Open {actionDef.module} →
                </button>
              </Section>
            )}

            {/* ── EVENTS RELEVANT TO YOUR HOLDINGS ────────────────── */}
            {events.length > 0 && (
              <Section title="Events Relevant to Your Holdings">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {events.map((evt, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10 }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--accent)',
                        marginTop: 2,
                        flexShrink: 0,
                      }}>·</span>
                      <div>
                        <div style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: 12,
                          color: 'var(--text-primary)',
                          lineHeight: 1.5,
                        }}>
                          {evt.title}
                        </div>
                        <div style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10,
                          color: 'var(--text-tertiary)',
                          marginTop: 2,
                        }}>
                          {new Date(evt.event_at).toLocaleDateString('en-GB', {
                            weekday: 'short', month: 'short', day: 'numeric',
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <div style={{ height: 24 }} />
          </>
        )}
      </div>
    </>
  )
}
